import { findIndex, findLastIndex } from "lodash";
import { getLineColor, prepareLegendDataItems, sortByResponseId, } from "ps-components/Plot/utils";
import { getArrayMinMax } from "utils/data";
import { PsCalculationResultsDataItemState, } from "store/ps-workspace/ps-workspace.types";
import { getYAxisName } from "store/ps-workspace/ps-workspace.utils";
const defaultEmptyData = { x: [null], y: [null], type: "scatter" };
const prepareInitialPlotData = ({ responses }, yDictionaryParameters, analiseOptions, configurationOptions) => {
    const legendDataItems = prepareLegendDataItems(responses, yDictionaryParameters, analiseOptions, configurationOptions);
    if (responses.length) {
        return responses
            .slice()
            .sort(sortByResponseId)
            .map(({ id, mergeId }, index) => ({
            ...defaultEmptyData,
            yaxis: `y${index ? index + 1 : ""}`,
            responseId: id,
            mergeId,
            line: {
                color: getLineColor(id),
            },
            legendData: legendDataItems[id] ?? {},
        }));
    }
    else {
        return [defaultEmptyData];
    }
};
const getTrimmedXYValues = (data) => {
    const { x, y } = data;
    const firstValidIndex = findIndex(y, (v) => v !== null);
    const lastValidIndex = findLastIndex(y, (v) => v !== null) + 1;
    const trimmedX = x.slice(firstValidIndex, lastValidIndex);
    const trimmedY = y.slice(firstValidIndex, lastValidIndex);
    return { x: trimmedX, y: trimmedY };
};
const preparePlotData = (prevState, storedLineGraphLines, { responses }, yDictionaryParameters) => {
    const preparedData = storedLineGraphLines.reduce((arr, resultItem) => {
        const { responseId } = resultItem;
        const resultItemData = resultItem?.data ?? [];
        const stateText = resultItem?.dataTopState ?? "Undefined results";
        const stateMessage = resultItem.dataTopStateMessage;
        const yPointsLength = resultItemData.map(({ yWithConfidence }) => yWithConfidence.length, []);
        const maxYPointsLength = yPointsLength.length ? getArrayMinMax(yPointsLength).max : 1;
        const yLines = resultItemData.reduce((arr, { yWithConfidence }) => {
            Array.from(Array(maxYPointsLength)).forEach((_, index) => {
                const y = yWithConfidence[index] ? (yWithConfidence[index].y ?? null) : null;
                arr[index] ? arr[index].push(y) : arr.splice(index, 0, [y]);
            });
            return arr;
        }, []) ?? [];
        const xValues = resultItemData.map(({ x }) => x);
        const isLoading = resultItemData.some(({ state }) => state === PsCalculationResultsDataItemState.Pending);
        const { yParameterId, yUnitsId, color, yAutoscaling, locked, visible, mergeId } = responses.find(({ id }) => responseId === id) ?? {};
        const yAxisUnit = yParameterId && yUnitsId
            ? (yDictionaryParameters[yParameterId]?.units.find(({ id }) => id === yUnitsId)?.name ?? "")
            : "";
        let yLinesPlotData = [];
        const lineColor = getLineColor(responseId, color?.hexValue, isLoading);
        if (!yLines.length) {
            const prevStateItem = prevState.find(({ responseId: id }) => responseId === id);
            if (prevStateItem) {
                const { line, y } = prevStateItem;
                yLinesPlotData = [
                    {
                        ...prevStateItem,
                        isLoading,
                        ...(isLoading ? { y } : { y: [null], warning: true }),
                        stateText,
                        stateMessage,
                        line: { ...line, color: lineColor },
                        hovertemplate: `${yAxisUnit}<extra></extra>`,
                        fixedrange: locked,
                        locked,
                        mergeId,
                        autorange: yAutoscaling,
                        visible: visible || "legendonly",
                    },
                ];
            }
            else {
                yLinesPlotData = [
                    {
                        ...defaultEmptyData,
                        responseId,
                        isLoading,
                        autorange: yAutoscaling,
                        fixedrange: locked,
                        mergeId,
                        locked,
                        stateText,
                        stateMessage,
                        line: {
                            width: 1.25,
                            color: lineColor,
                        },
                        visible: visible || "legendonly",
                        hovertemplate: `${yAxisUnit}<extra></extra>`,
                    },
                ];
            }
        }
        else {
            yLinesPlotData = yLines.map((yValues, index) => ({
                ...getTrimmedXYValues({ x: xValues, y: yValues }),
                mode: "lines+markers",
                marker: {
                    size: 4,
                    opacity: yValues.filter((v) => v !== null).length <= 1 ? 1 : 0,
                },
                hovertemplate: `${yAxisUnit}<extra></extra>`,
                line: {
                    shape: "spline",
                    color: lineColor,
                    ...(!!index && { dash: "dot" }),
                },
                type: "scatter",
                responseId,
                stateText,
                stateMessage,
                mergeId,
                showlegend: true,
                visible: visible || "legendonly",
                isAdditionalLine: !!index,
                isLoading,
                autorange: yAutoscaling,
                locked,
                fixedrange: locked,
            }));
        }
        return [...arr, ...yLinesPlotData];
    }, []);
    const preparedLinesIds = preparedData
        .map(({ responseId }) => responseId)
        .filter((val, index, self) => val !== undefined && self.indexOf(val) === index);
    const responsesIds = responses.map(({ id }) => id);
    const filteredState = prevState.filter(({ responseId: id }) => id && !preparedLinesIds.includes(id) && responsesIds.includes(id));
    const preparedPlotData = [...filteredState, ...preparedData]
        .slice()
        .sort(sortByResponseId)
        .map((item, index, array) => ({
        ...item,
        yaxis: getYAxisName(item, array),
    }));
    return preparedPlotData.length ? preparedPlotData : [defaultEmptyData];
};
export { preparePlotData, prepareInitialPlotData };
